import { useMemo, useState } from 'react'
import type { Snapshot } from '@/api/types/readiness'
import { scoreBand } from '@/lib/readiness/score'
import { RdModal } from '@/components/readiness/RdModal'
import { FilterSelect } from '@/components/readiness/FilterSelect'
import { Chip, Icon } from '@/components/readiness/primitives'
import { savedBy } from '@/lib/readiness/compare'
import { OriginLink } from '@/lib/readiness/nav'
import { formatDate } from '@/components/common/format'

// ── View all saved assessments (I6 fix §4) ──────────────────────────────────
// An 880px modal listing every saved assessment, newest first, with search
// (name or note), a Saved by filter (People | System) and a Year filter. `Set
// as From` closes the modal and updates the compared pair everywhere; `View`
// opens the saved assessment's report with `← Progress` as the back link.

export function ViewAllAssessmentsModal({ snapshots, orgName, scale, fromId, fmt, q, onClose, onSetFrom }: {
  snapshots: Snapshot[]
  orgName: string
  scale: 'levels' | 'score_0_10'
  fromId: string
  fmt: (v: number) => string
  q: string
  onClose: () => void
  onSetFrom: (id: string) => void
}) {
  const [search, setSearch] = useState('')
  const [by, setBy] = useState('all')
  const [year, setYear] = useState('all')
  const newestFirst = useMemo(() => [...snapshots].reverse(), [snapshots])
  const asOf = (s: Snapshot) => s.as_of ?? s.created_at
  const years = useMemo(() => Array.from(new Set(newestFirst.map((s) => asOf(s).slice(0, 4)))).sort((a, b) => b.localeCompare(a)), [newestFirst])
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return newestFirst.filter((s) => {
      if (needle && !s.name.toLowerCase().includes(needle) && !(s.note ?? '').toLowerCase().includes(needle)) return false
      if (by === 'people' && s.created_by === 'system') return false
      if (by === 'system' && s.created_by !== 'system') return false
      if (year !== 'all' && asOf(s).slice(0, 4) !== year) return false
      return true
    })
  }, [newestFirst, search, by, year])
  const first = snapshots[0]

  return (
    <RdModal
      title="Saved assessments"
      subtitle={`${orgName || 'Organisation'} · ${snapshots.length} saved${first ? ` since ${formatDate(asOf(first))}` : ''} · newest first`}
      width={880}
      onClose={onClose}
      testId="view-all-modal"
      labelledBy="rd-view-all-title"
      bodyClassName="p-0"
      footerClassName="justify-between"
      footerStyle={{ background: 'var(--rd-bg-header)', borderRadius: '0 0 14px 14px' }}
      footer={
        <>
          <p className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="view-all-showing">Showing {rows.length} of {snapshots.length}</p>
          <button type="button" className="rd-btn" onClick={onClose}>Close</button>
        </>
      }
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-6 py-3" style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
        <label className="relative min-w-[200px] flex-1">
          <Icon name="search" size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--rd-text-faint)' }} />
          <input type="search" className="rd-input !h-9 !py-0 !pl-9 !text-[13px]" placeholder="Search by name or note" aria-label="Search by name or note" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="view-all-search" />
        </label>
        <FilterSelect compact label="Saved by" value={by} onChange={setBy} defaultValue="all" options={[['all', 'All'], ['people', 'People'], ['system', 'System']]} testId="view-all-by" />
        <FilterSelect compact label="Year" value={year} onChange={setYear} defaultValue="all" options={[['all', 'All'], ...years.map((y): [string, string] => [y, y])]} testId="view-all-year" />
      </div>

      {/* Body */}
      <div className="rd-scroll-thin overflow-y-auto" style={{ maxHeight: 470, scrollbarGutter: 'stable' }} data-testid="view-all-body">
        <table className="rd-va-table w-full text-[13px]" style={{ tableLayout: 'fixed' }} data-testid="view-all-table">
          <colgroup>
            <col />
            <col style={{ width: 112 }} />
            <col style={{ width: 136 }} />
            <col style={{ width: 124 }} />
            <col style={{ width: 236 }} />
          </colgroup>
          <thead>
            <tr className="text-[12px] font-semibold" style={{ color: 'var(--rd-text-muted)' }}>
              <th className="px-6 text-left">Assessment</th>
              <th className="px-3 text-left">As of</th>
              <th className="px-3 text-left">Saved by</th>
              <th className="px-3 text-left">Score</th>
              <th className="px-3 pr-6 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-6 text-center text-[13px]" style={{ color: 'var(--rd-text-muted)' }}>No saved assessments match.</td></tr>
            )}
            {rows.map((s) => {
              const isFrom = s.id === fromId
              const band = scoreBand(s.score)
              return (
                <tr key={s.id} style={{ borderTop: '1px solid var(--rd-border-subtle)' }} data-testid={`view-all-row-${s.id}`}>
                  <td className="px-6 py-2 align-middle">
                    <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 font-semibold leading-[1.3]"><span className="min-w-0">{s.name}</span>{s.baseline && <Chip tone="chip" className="rd-chip-sm">BASELINE</Chip>}{isFrom && <span className="rd-pair-tag" data-testid={`view-all-tag-${s.id}`}>FROM</span>}</p>
                    {s.note && <p className="rd-rail-note text-[12px]" title={s.note}>{s.note}</p>}
                  </td>
                  <td className="rd-mono whitespace-nowrap px-3 align-middle text-[12.5px]">{formatDate(asOf(s))}</td>
                  <td className="truncate px-3 align-middle" title={savedBy(s)}>{savedBy(s)}</td>
                  <td className="whitespace-nowrap px-3 align-middle"><span className="rd-mono font-bold">{fmt(s.score)}</span>{scale === 'levels' && <> <span className="text-[12px] font-semibold" style={{ color: band.token }}>{band.label}</span></>}</td>
                  <td className="whitespace-nowrap px-3 pr-6 align-middle text-[12.5px]">
                    <span className="flex flex-wrap gap-x-3 gap-y-1">
                      <OriginLink to={`/readiness/report/${s.id}${q}`} className="rd-link" data-testid={`view-all-view-${s.id}`}>View</OriginLink>
                      <OriginLink to={`/readiness/report/${s.id}${q}&print=1`} className="rd-link" originKey={`export-${s.id}`}>Export PDF</OriginLink>
                      {isFrom ? <span style={{ color: 'var(--rd-text-faint)' }}>Comparing from</span> : <button type="button" className="rd-link" onClick={() => onSetFrom(s.id)} data-testid={`view-all-set-from-${s.id}`}>Set as From</button>}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </RdModal>
  )
}
